import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HostBridgeService } from '../services/ai-assistant/host-bridge.service';
import { UserAuthService } from '../services/user-auth/user-auth.service';

type AiAssistantViewMode = 'floating' | 'vertical';

@Component({
  selector: 'app-ai-assistant-widget',
  templateUrl: './ai-assistant-widget.component.html',
  styleUrls: ['./ai-assistant-widget.component.scss'],
})
export class AiAssistantWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('assistantFrame') assistantFrame?: ElementRef<HTMLIFrameElement>;

  public isOpen = false;
  public isMaximized = false;
  public viewMode: AiAssistantViewMode = 'floating';
  public followMode = false;
  public readonly title = 'ai_assistant';
  public readonly assistantIconPath = 'assets/icon/AI_Assistant.png';
  public readonly assistantUrl = this.buildAssistantUrl();
  public readonly trustedAssistantUrl: SafeResourceUrl;

  private readonly assistantOrigin: string;
  private readonly subscriptions = new Subscription();
  private currentSessionAccessToken = '';

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly userAuthService: UserAuthService,
    private readonly hostBridge: HostBridgeService,
    private readonly router: Router
  ) {
    this.trustedAssistantUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.assistantUrl);
    this.assistantOrigin = new URL(this.assistantUrl).origin;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.hostBridge.followMode$.subscribe((enabled) => (this.followMode = enabled))
    );
    // Keeps the assistant pointed at whatever project the user navigated to, so
    // it never has to guess and the two sides cannot drift apart.
    this.subscriptions.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => this.pushCurrentProject())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.hostBridge.disconnect();
    this.currentSessionAccessToken = '';
  }

  public openAssistant(viewMode: AiAssistantViewMode = 'floating'): void {
    this.viewMode = viewMode;
    this.isOpen = true;
    this.currentSessionAccessToken = this.userAuthService.getAccessToken() || '';
    this.postTokenToAssistant();
  }

  public closeAssistant(): void {
    this.isOpen = false;
    this.isMaximized = false;
    this.viewMode = 'floating';
    this.currentSessionAccessToken = '';
    this.hostBridge.disconnect();
  }

  public toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
  }

  public toggleSidePanel(): void {
    this.viewMode = this.viewMode === 'vertical' ? 'floating' : 'vertical';
    this.isMaximized = false;
  }

  public toggleFollowMode(): void {
    this.hostBridge.toggleFollowMode();
  }

  public onIframeLoad(): void {
    if (this.isOpen) {
      this.postTokenToAssistant();
    }
  }

  private buildAssistantUrl(): string {
    const url = new URL(environment.aiAssistantUrl);
    url.searchParams.set('embedded', 'true');
    url.searchParams.set('apiUrl', environment.tokenEndpoint);
    return url.toString();
  }

  private postTokenToAssistant(): void {
    const accessToken = this.currentSessionAccessToken;
    const frame = this.assistantFrame?.nativeElement;
    const targetWindow = frame?.contentWindow;

    if (!accessToken || !frame || !targetWindow) {
      return;
    }

    // Connect before the first post: the assistant learns where to send events
    // back from the origin of the first message it receives, so nothing it emits
    // is addressed until one has gone out.
    this.hostBridge.connect(frame, this.assistantOrigin);

    targetWindow.postMessage(
      {
        type: 'AUTH_TOKEN',
        payload: {
          token: accessToken,
          apiUrl: environment.tokenEndpoint,
        },
      },
      this.assistantOrigin
    );

    targetWindow.postMessage(
      {
        type: 'SET_API_URL',
        apiUrl: environment.tokenEndpoint,
        payload: {
          apiUrl: environment.tokenEndpoint,
        },
      },
      this.assistantOrigin
    );

    targetWindow.postMessage(
      {
        type: 'BLUEPRINT_AUTH_TOKEN',
        token: accessToken,
        accessToken,
        bearerToken: `Bearer ${accessToken}`,
        apiUrl: environment.tokenEndpoint,
      },
      this.assistantOrigin
    );

    this.pushCurrentProject();
  }

  /** Reads the project out of the current URL — every project-scoped screen in
   *  the app is routed as `.../project-detail/:id`. */
  private pushCurrentProject(): void {
    const match = this.router.url.match(/\/project-detail\/([^/?#]+)/);
    if (match) {
      this.hostBridge.pushProject(decodeURIComponent(match[1]));
    }
  }
}
